import React, { useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { Loading } from '@arco-design/mobile-react';
import { BusinessCode } from '@/http/config';
import Empty from '@/components/Empty';
import * as dd from 'dingtalk-jsapi';
import { ContextProvider } from '@arco-design/mobile-react';
import styles from './index.module.less';
import Device from '@/utils/device';
interface IMobilePageProps {
  title: string;
  pageLoading?: boolean;
  errorCode?: BusinessCode;
}
const MobilePage = (props: React.PropsWithChildren<IMobilePageProps>) => {
  const { title, pageLoading, errorCode } = props;

  // 动态加载移动端样式，只在移动端加载
  useEffect(() => {
    if (!Device.isMobile()) {
      return; // PC 端不加载移动端样式
    }

    // 检查是否已经加载过样式（通过检查 head 中是否有对应的 link 标签）
    const existingLink = document.getElementById('arco-mobile-style');
    if (existingLink) {
      return; // 样式已加载，直接返回
    }

    // 使用 webpack 的动态 import，让 webpack 在构建时处理路径和 hash
    // webpack 会将 CSS 提取为独立的 chunk，并在运行时动态加载
    // 这样在生产环境中，webpack 会自动处理 CSS 文件的路径和 hash
    // @ts-ignore - TypeScript 不支持直接 import CSS，但 webpack 会处理
    import('@arco-design/mobile-react/dist/style.css')
      .then(() => {
        // 动态 import CSS 后，webpack 会自动注入样式
        // 这里创建一个标记，避免重复加载
        const marker = document.createElement('meta');
        marker.id = 'arco-mobile-style';
        marker.name = 'arco-mobile-style-loaded';
        document.head.appendChild(marker);
      })
      .catch((err) => {
        console.warn('Failed to load mobile styles:', err);
        // 降级方案：尝试从 node_modules 加载（仅开发环境）
        if (process.env.NODE_ENV === 'development') {
          const link = document.createElement('link');
          link.id = 'arco-mobile-style';
          link.rel = 'stylesheet';
          link.href = '/node_modules/@arco-design/mobile-react/dist/style.css';
          document.head.appendChild(link);
        }
      });
  }, []);

  useEffect(() => {
    if (dd.env.platform !== 'notInDingTalk') {
      dd.ready(function () {
        dd.biz.navigation.setTitle({
          title: title, //控制标题文本，空字符串表示显示默认文本
        });
      });
    }
  }, [title]);
  const errConfig = useMemo(() => {
    if (!errorCode) {
      return;
    }
    let type;
    let text;
    if (errorCode === BusinessCode.NO_PERMISSION) {
      type = 'noPermission';
    } else if (errorCode === BusinessCode.DEAD_LINK) {
      type = 'noExist';
      text = '链接已失效';
    } else {
      type = 'noExist';
    }
    return {
      type: type as any,
      text: text,
    };
  }, [errorCode]);
  return (
    <ContextProvider prefixCls={'arco-mobile'}>
      <div className={styles.mobilePage}>
        <Helmet>
          <meta charSet="UTF-8" />
          <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover"
          />
          <meta name="format-detection" content="telephone=no" />
          <meta name="apple-mobile-web-app-capable" content="yes" />
          <title>{title}</title>
        </Helmet>
        {pageLoading ? (
          <div
            style={{
              display: 'flex',
              height: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Loading type="dot" />
          </div>
        ) : errorCode ? (
          <div style={{ height: '80vh' }}>
            <Empty type={errConfig?.type} text={errConfig?.text} center />
          </div>
        ) : (
          props.children
        )}
      </div>
    </ContextProvider>
  );
};

export default MobilePage;
